/**
 * @file app/tauri/cashDrawer.js
 *
 * Cash-register / cash-drawer message builder for the Tauri (desktop) build.
 *
 * Faithful port of the SAM4s + generic Fiscat/AEE fiscal QR string builders.
 * These are pure string builders (no Node APIs); the resulting string is written
 * to the serial port via the Rust `send_to_serial` command. Ported from
 * Happy-POS `src/tauri/cashDrawer.js`.
 */

/** VAT-key comparator used to order the SAM4s gyűjtő lines. */
export const sortVatKeys = (a, b) => {
  const numA = !isNaN(a);
  const numB = !isNaN(b);
  if (numA && numB) return Number(a) - Number(b);
  if (numA) return -1;
  if (numB) return 1;
  const wordA = /^[a-zA-Z]{2}/.test(a);
  const wordB = /^[a-zA-Z]{2}/.test(b);
  if (wordA && wordB) return a.replace("Sz", "") - b.replace("Sz", "");
  if (wordA || wordB) return (wordA && -1) || 1;
  return 1;
};

/** SAM4s fiscal VAT category code for a VAT percentage / service-fee key. */
export const getSam4sVATCode = (vatPercentage) => {
  const v = String(vatPercentage);
  if (v === "5" || v === "Sz5") return "A00";
  if (v === "18" || v === "Sz18") return "B00";
  if (v === "27" || v === "Sz27") return "C00";
  if (v === "0" || v === "Sz0") return "E00";
  return "C00";
};

/**
 * Hungarian "5-ös kerekítés" cash rounding to the nearest 0/5 forint.
 */
export const hungarianRoundNumbers = (price = 0) => {
  const tags = (Number(price) / 10).toFixed(1).split(".");
  let firstTag = parseInt(tags[0], 10) || 0;
  let lastTag = parseInt(tags[1], 10) || 0;
  if (lastTag === 5 || lastTag === 0) return Number(price);
  if (lastTag === 3 || lastTag === 4 || lastTag === 6 || lastTag === 7) lastTag = 5;
  else if (lastTag === 8 || lastTag === 9) {
    lastTag = 0;
    firstTag += 1;
  } else lastTag = 0;
  return firstTag * 10 + lastTag;
};

/**
 * Build the SAM4s `PTG|...` QR command string.
 * @returns {string|null} null when there is nothing to print.
 */
export const generateSam4sQRCodeString = (storedOrder = {}, grossSummary = {}) => {
  const summary =
    grossSummary && Object.keys(grossSummary).length > 0
      ? grossSummary
      : storedOrder.grossSum || storedOrder.grossSummary || {};

  if (!storedOrder.orderCode || Object.keys(summary).length === 0) return null;

  const normalVats = {};
  const serviceFeeVats = {};
  Object.keys(summary).forEach((vatKey) => {
    const vatValue = summary[vatKey] || 0;
    if (!vatValue) return;
    if (String(vatKey).includes("Sz")) serviceFeeVats[vatKey] = vatValue;
    else normalVats[vatKey] = vatValue;
  });

  const vats = [];
  Object.keys(normalVats)
    .sort(sortVatKeys)
    .forEach((vatKey) => {
      vats.push(`1|Gyujto ${vatKey}%||${summary[vatKey]}|1|db|${summary[vatKey]}|${getSam4sVATCode(vatKey)}||`);
    });
  Object.keys(serviceFeeVats)
    .sort(sortVatKeys)
    .forEach((vatKey) => {
      vats.push(
        `1|Szervízdíj ${String(vatKey).replace("Sz", "")}%||${summary[vatKey]}|1|db|${summary[vatKey]}|${getSam4sVATCode(vatKey)}||`
      );
    });

  const paymentTypes =
    storedOrder.paymentTypes && storedOrder.paymentTypes.length > 0
      ? storedOrder.paymentTypes
      : [{ paymentType: storedOrder.paymentType || "cash", amount: storedOrder.orderSum || storedOrder.totalPrice || 0 }];

  const paymentString = paymentTypes
    .filter((p) => Number(p.amount || 0) > 0)
    .map(
      (p) =>
        `6|${p.paymentType === "cash" ? "1" : "2"}|${p.paymentType === "cash" ? hungarianRoundNumbers(p.amount) : p.amount}`
    );

  if (vats.length === 0 && paymentString.length === 0) return null;

  return `PTG|1\\0|Rendeles azonosito|#${storedOrder.orderCode}\\${vats.join("\\\\")}\\${paymentString.join("\\\\")}`;
};

/**
 * Build the generic (FISCAT/AEE) QR command string.
 * @returns {string|null} null when there is nothing to print.
 */
export const generateQRCodeString = (storedOrder = {}) => {
  const { orderCode, items, orderSum } = storedOrder;
  if (!items || items.length === 0) return null;

  const hasNonZeroPrice = items.some((item) => (item.remotePrice || item.price || 0) > 0);
  if (!hasNonZeroPrice) return null;

  let qrCodeString = "";
  const discountPercentage = storedOrder?.discounts?.percentage || 0;
  const serviceFees = {};

  const getVATCode = (vatPercentage) => {
    if (vatPercentage === 5) return 1;
    if (vatPercentage === 18) return 2;
    if (vatPercentage === 27) return 3;
    if (vatPercentage === 0) return 5;
    return 3;
  };

  let rowsCount = 0;

  [...items].forEach((item) => {
    const itemName = item.remoteName || item.name?.hu || item.name || "GYŰJTŐ";
    const vatPercentage = item?.item?.vat?.[storedOrder.orderType] || 27;
    const vatCode = getVATCode(vatPercentage);
    const itemPrice = item.remotePrice || item.price || 0;
    const itemQuantity = item.amount || item.quantity || 1;

    if (storedOrder.serviceFee > 0) {
      const itemServiceFee = ((itemPrice * itemQuantity) / 100) * storedOrder.serviceFee;
      if (!serviceFees[vatPercentage]) serviceFees[vatPercentage] = 0;
      serviceFees[vatPercentage] += itemServiceFee;
    }

    rowsCount++;
    qrCodeString += `${itemName}|${vatCode}|${itemPrice}|${itemQuantity}|${discountPercentage > 0 ? "M" + discountPercentage : ""}|`;

    if (Array.isArray(item.extras) && item.extras.length > 0) {
      item.extras.forEach((extra) => {
        const extraPrice = extra.remotePrice || extra.price || 0;
        const extraName = extra.remoteName || extra?.item?.name?.hu || "GYŰJTŐ";
        if (extraPrice > 0) {
          rowsCount++;
          qrCodeString += ` +${extraName}|${vatCode}|${extraPrice}|${itemQuantity}|${discountPercentage > 0 ? "M" + discountPercentage : ""}|`;
        }
      });
    }
  });

  if (
    (storedOrder.discountAmount && storedOrder.discountAmount > 0) ||
    (storedOrder.discountPrice && storedOrder.discountPrice > 0 && !storedOrder?.discounts?.percentage)
  ) {
    rowsCount++;
    qrCodeString += `Kedvezmény|3|${(storedOrder.discountAmount || storedOrder.discountPrice) * -1}|1||`;
  }

  if (storedOrder.serviceFee > 0 || storedOrder.serviceFeePrice > 0) {
    Object.keys(serviceFees).forEach((vatRate) => {
      const vatCode = getVATCode(Number(vatRate));
      const serviceFeeAmount = serviceFees[vatRate].toFixed(0);
      if (serviceFeeAmount > 0) {
        rowsCount++;
        qrCodeString += `Szervízdíj ${vatRate}%|${vatCode}|${serviceFeeAmount}|1||`;
      }
    });
  }

  if (storedOrder.deliveryFee && storedOrder.deliveryFee > 0) {
    rowsCount++;
    qrCodeString += `Szállítási díj|3|${storedOrder.deliveryFee}|1||`;
  }

  if (storedOrder.paymentTypes && storedOrder.paymentTypes.length > 0) {
    storedOrder.paymentTypes.forEach((payment) => {
      const paymentType = payment.paymentType === "cash" ? "P" : "N";
      const paymentAmount =
        (payment.paymentType === "cash" ? hungarianRoundNumbers(payment.amount) : payment.amount) || 0;
      qrCodeString += `${paymentType}|${paymentAmount}`;
    });
  } else {
    const paymentTypeCode = storedOrder.paymentType === "cash" ? "P" : "N";
    const paymentAmount = orderSum || "";
    qrCodeString += `${paymentTypeCode}|${storedOrder.paymentType === "cash" ? hungarianRoundNumbers(paymentAmount) : paymentAmount}`;
  }

  return `fiscat/AEE|SLD||${String(orderCode).replace("-", "")}|6|Rendelés száma:|#${orderCode}|-------------|www.happygastro.hu|Üzemelteti a|Happy Gastro||||${rowsCount}|${qrCodeString}`;
};

/**
 * Resolve the serial message string for a cash-drawer/cash-register send.
 * @param {{config?:object, order?:object, grossSummary?:object}} content
 * @returns {string|null}
 */
export const buildCashDrawerMessage = (content = {}) => {
  const { config = {}, order = {}, grossSummary = {} } = content;
  const type = String(config.type || "").toLowerCase();
  if (type.includes("sam4s")) return generateSam4sQRCodeString(order, grossSummary);
  return generateQRCodeString(order);
};

export default { buildCashDrawerMessage, generateSam4sQRCodeString, generateQRCodeString };
